import { createSelector } from "@reduxjs/toolkit";
import { shallowEqual } from "react-redux";
import type {
    SubtaskFacingsPayload,
    SubtaskFinGroupPhotosPayload,
    SubtaskProductsMatrixPayload,
} from "tm-shared/types";
import {
    isSubtaskFacingsPayload,
    isSubtaskFinGroupPhotosEntity,
    isSubtaskProductsMatrixPayload,
} from "tm-shared/utils";

import { useAppSelector } from "@/hooks/redux";
import type { RootState } from "@/store";
import { SubtaskConfig, TaskConfig } from "@/store/slices/ui";

interface GetAssortmentSubtaskArgs {
    taskId: string;
    subtaskId: string;
}

interface GetTaskConfigArgs {
    pointId: string;
    taskId: string;
}
interface GetSubtaskConfigArgs extends GetTaskConfigArgs {
    subtaskId: string;
}

// ---------------------------- Assortment

const selectAssortment = (s: RootState) => s.progress.assortmentMatrix;

const makeSelectAssortmentSubtask = (taskId: string, subtaskId: string) =>
    createSelector([selectAssortment], (assortment) => {
        return assortment?.[taskId]?.[subtaskId];
    });

const makeSelectSubtaskFinGroupPhotos = (taskId: string, subtaskId: string) =>
    createSelector(
        [makeSelectAssortmentSubtask(taskId, subtaskId)],
        (subtask) => (isSubtaskFinGroupPhotosEntity(subtask) ? subtask : null),
    );

export const useSubtaskFinGroupPhotosSelector = ({
    taskId,
    subtaskId,
}: GetAssortmentSubtaskArgs): SubtaskFinGroupPhotosPayload | null => {
    return useAppSelector(makeSelectSubtaskFinGroupPhotos(taskId, subtaskId));
};

const makeSelectSubtaskProductsMatrix = (taskId: string, subtaskId: string) =>
    createSelector(
        [makeSelectAssortmentSubtask(taskId, subtaskId)],
        (subtask) => (isSubtaskProductsMatrixPayload(subtask) ? subtask : null),
    );

export const useSubtaskProductsMatrixSelector = ({
    taskId,
    subtaskId,
}: GetAssortmentSubtaskArgs): SubtaskProductsMatrixPayload | null => {
    return useAppSelector(makeSelectSubtaskProductsMatrix(taskId, subtaskId));
};

const makeSelectSubtaskFacings = (taskId: string, subtaskId: string) =>
    createSelector(
        [makeSelectAssortmentSubtask(taskId, subtaskId)],
        (subtask) => (isSubtaskFacingsPayload(subtask) ? subtask : null),
    );

export const useSubtaskFacingsSelector = ({
    taskId,
    subtaskId,
}: GetAssortmentSubtaskArgs): SubtaskFacingsPayload | null => {
    return useAppSelector(makeSelectSubtaskFacings(taskId, subtaskId));
};

// ---------------------------- UI config

const selectTasksConfig = (s: RootState) => s.ui.mdTasksConfig;

const makeSelectTasksConfig = (pointId: string, taskId: string) =>
    createSelector([selectTasksConfig], (tasksConfig): TaskConfig | null => {
        return tasksConfig?.[pointId]?.[taskId] ?? null;
    });

export const useTaskConfigSelector = ({
    pointId,
    taskId,
}: GetTaskConfigArgs) => {
    return (
        useAppSelector(makeSelectTasksConfig(pointId, taskId), shallowEqual) ??
        {}
    );
};

const makeSelectSubtaskConfig = (
    pointId: string,
    taskId: string,
    subtaskId: string,
) =>
    createSelector([selectTasksConfig], (tasksConfig): SubtaskConfig | null => {
        return tasksConfig?.[pointId]?.[taskId]?.subtasks?.[subtaskId] ?? null;
    });

export const useSubtaskConfigSelector = ({
    pointId,
    taskId,
    subtaskId,
}: GetSubtaskConfigArgs) => {
    return (
        useAppSelector(
            makeSelectSubtaskConfig(pointId, taskId, subtaskId),
            shallowEqual,
        ) ?? {}
    );
};
